import { Component, input, output } from '@angular/core';
import { AppRoutes, FundTypes, NumberFormatConfig } from '@gs-2.0/core';
import { FundTransaction } from './fund-cash-summary.types';

/**
 * Fund Cash Summary Component
 *
 * Displays a financial transaction summary card with categorized entries showing
 * transaction types, amounts, and dates. Uses content-card as a container.
 *
 * Figma: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36115
 */
@Component({
  selector: 'app-fund-cash-summary',
  standalone: false,
  templateUrl: './fund-cash-summary.component.html',
  styleUrl: './fund-cash-summary.component.scss',
})
export class FundCashSummaryComponent {
  /**
   * Array of transaction data to display
   */
  transactions = input.required<FundTransaction[]>();

  /**
   * Fund type for the current fund (DAF, CHARITY, or PROJECT)
   * Used to determine display text for transactions
   */
  fundType = input.required<FundTypes>();

  /**
   * Title of the summary card
   * @default "fund.cashSummary.title"
   */
  title = input<string>('fund.cash_summary.title');

  /**
   * Show/hide the "View All" button
   * @default true
   */
  showViewAll = input<boolean>(true);

  /**
   * Date range text for the footer badge
   * @default "fund.cashSummary.dateRange"
   */
  dateRangeText = input<string>('fund.cash_summary.date_range');

  /**
   * Disclaimer text shown in footer
   * @default "fund.cashSummary.disclaimer"
   */
  disclaimerText = input<string>('fund.cash_summary.disclaimer');

  /**
   * Link text for the activity tab
   * @default "fund.cashSummary.activityTab"
   */
  activityLinkText = input<string>('fund.cash_summary.activity_tab');

  /**
   * URL for the "View All" button
   * @default "#"
   */
  viewAllUrl = input<string>(AppRoutes.FUND_ACTIVITY);

  /**
   * Background color for the content card
   * @default undefined (uses default token value)
   */
  backgroundColor = input<string | undefined>('#F3F3F352');

  /**
   * Number format configuration from global config
   */
  numberFormat = input<NumberFormatConfig>({
    decimalSymbol: '.',
    digitGroupingSymbol: ',',
    digitGrouping: '123,456,789',
  });

  /**
   * Date format from global config (e.g. 'yyyy-MM-dd', 'MM/dd/yyyy', 'dd-MM-yyyy')
   */
  dateFormat = input<string>('MM/dd/yyyy');

  /**
   * Emits when "View All" button is clicked
   */
  viewAllClicked = output<void>();

  /**
   * Emits when activity link is clicked
   */
  activityLinkClicked = output<void>();

  // Translation key constants
  private readonly TRANSLATION_KEYS = {
    CONTRIBUTIONS_TO: 'fund.cash_summary.transactions.contributions_to',
    TRANSFERS_TO: 'fund.cash_summary.transactions.transfers_to',
    TRANSFERS_FROM: 'fund.cash_summary.transactions.transfers_from',
    GRANTS: 'fund.cash_summary.transactions.grants',
    DISTRIBUTIONS: 'fund.cash_summary.transactions.distributions',
    OTHER_ACTIVITIES: 'fund.cash_summary.transactions.other_activities',
  } as const;

  // Description pattern mappings for DAF funds
  private readonly DAF_DESCRIPTION_PATTERNS: Array<{ pattern: RegExp; key: string }> = [
    { pattern: /CONTRIBUTION\s+FROM/i, key: this.TRANSLATION_KEYS.CONTRIBUTIONS_TO },
    { pattern: /GRANT\s+TO/i, key: this.TRANSLATION_KEYS.GRANTS },
  ];

  // Description pattern mappings for CHARITY/PROJECT funds
  private readonly CHARITY_PROJECT_DESCRIPTION_PATTERNS: Array<{ pattern: RegExp; key: string }> = [
    { pattern: /GRANT\s+TO/i, key: this.TRANSLATION_KEYS.DISTRIBUTIONS },
    { pattern: /CONTRIBUTION\s+FROM/i, key: this.TRANSLATION_KEYS.CONTRIBUTIONS_TO },
  ];

  /**
   * Handle "View All" button click
   */
  onViewAllClick(): void {
    this.viewAllClicked.emit();
  }

  /**
   * Handle activity link click
   */
  onActivityLinkClick(): void {
    this.activityLinkClicked.emit();
  }

  /**
   * Check if transaction is positive (income) based on amount
   * @param amount - The transaction amount
   * @returns True if amount is positive (income), false if negative (expense)
   */
  isPositiveTransaction(amount: number): boolean {
    return amount > 0;
  }

  /**
   * Format amount with currency symbol and thousands separators
   * @param amount - The amount to format
   * @param currency - Currency code (e.g., 'USD')
   * @returns Formatted amount string
   */
  formatAmount(amount: number, currency: string): string {
    const fmt = this.numberFormat();
    const parts = Math.abs(amount).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart, fmt.digitGroupingSymbol, fmt.digitGrouping);
    const formatted = grouped + fmt.decimalSymbol + decPart;
    const sign = amount < 0 ? '-' : '';
    return `${sign}${formatted} ${currency}`;
  }

  private applyDigitGrouping(intPart: string, groupSymbol: string, groupPattern: string): string {
    const patternParts = groupPattern.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;
    const groupSizes = patternParts.map(p => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;
    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(digits.slice(pos, pos + size).reverse().join(''));
      pos += size;
      sizeIdx++;
    }
    return groups.reverse().join(groupSymbol);
  }

  /**
   * Format date in MM / DD / YYYY format
   * @param date - Date string or Date object
   * @returns Formatted date string
   */
  formatDate(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const format = this.dateFormat();
    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    return format
      .replace(/yyyy/g, String(year))
      .replace(/yy/g, String(year).slice(-2))
      .replace(/MMM/g, '\x01')
      .replace(/MM/g, String(month).padStart(2, '0'))
      .replace(/M(?!M)/g, String(month))
      .replace(/dd/g, String(day).padStart(2, '0'))
      .replace(/d(?!d)/g, String(day))
      .replace(/\x01/g, ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month - 1]);
  }

  /**
   * Get translation key for transaction based on fund type and transaction details
   * @param transaction - The transaction object containing description
   * @returns Translation key to be used with translate pipe
   */
  getTransactionDisplayText(transaction: FundTransaction): string {
    const fundType = this.fundType();

    // DAF funds: use DAF-specific patterns
    if (fundType === FundTypes.DAF) {
      return this.matchDescriptionPattern(
        transaction,
        this.DAF_DESCRIPTION_PATTERNS
      );
    }

    // CHARITY/PROJECT funds: use CHARITY/PROJECT-specific patterns
    if (this.isCharityOrProjectFund(fundType)) {
      return this.matchDescriptionPattern(
        transaction,
        this.CHARITY_PROJECT_DESCRIPTION_PATTERNS
      );
    }

    // Fallback for unknown fund types
    return this.TRANSLATION_KEYS.OTHER_ACTIVITIES;
  }

  /**
   * Match transaction description against a list of patterns
   * @param transaction - The transaction object containing description and amount
   * @param patterns - Array of patterns to match against
   * @returns Translation key for the matched pattern, or OTHER_ACTIVITIES if no match
   */
  private matchDescriptionPattern(
    transaction: FundTransaction,
    patterns: Array<{ pattern: RegExp; key: string }>
  ): string {
    const normalizedDescription = transaction.description?.toUpperCase() || '';
    const isPositiveAmount = transaction.amount > 0;

    // Special handling for TRANSFER patterns based on amount
    if (/TRANSFER\s+(FROM|TO)/i.test(normalizedDescription)) {
      return isPositiveAmount
        ? this.TRANSLATION_KEYS.TRANSFERS_TO
        : this.TRANSLATION_KEYS.TRANSFERS_FROM;
    }

    // Continue with standard pattern matching for non-transfer patterns
    for (const { pattern, key } of patterns) {
      if (pattern.test(normalizedDescription)) {
        return key;
      }
    }

    return this.TRANSLATION_KEYS.OTHER_ACTIVITIES;
  }

  /**
   * Check if fund type is CHARITY, PROJECT, or PROJECT_CHARITY
   * @param fundType - The fund type to check
   * @returns True if the fund type is CHARITY, PROJECT, or PROJECT_CHARITY
   */
  private isCharityOrProjectFund(fundType: FundTypes): boolean {
    return [
      FundTypes.CHARITY,
      FundTypes.PROJECT,
      FundTypes.PROJECT_CHARITY
    ].includes(fundType);
  }
}
